import type { Column as TableColumn } from '@tanstack/react-table';
import { arrayMoveImmutable } from 'array-move';
import cloneDeep from 'lodash.clonedeep';
import SortableList, { SortableItem, SortableKnob } from 'react-easy-sort';
import { useTranslation } from 'react-i18next';
import { Button } from '../../../components/Button';
import { Column, Columns } from '../../../components/Columns';
import { FormGroup } from '../../../components/Form';
import { IconDragAndDropHandle } from '../../../components/Icon';
import InputCheckbox from '../../../components/InputCheckbox';
import { Handle } from '../../../components/OptionListV2/wrapper';
import Panel from '../../../components/Panel';
import { SeparatorList, SeparatorListItem } from '../../../components/SeparatorList';
import SortActions from '../../../components/SortActions';
import { StackMedium } from '../../../components/Stack';
import { Text } from '../../../components/Text';
import type { DataTableDataItem } from '..';

interface ColumnOrderPanelProps<T> {
  columns: TableColumn<T, unknown>[];
  isCurrentlyDefault: boolean;
  onOrderChange: (columns: TableColumn<T, unknown>[]) => void;
  onClose: () => void;
  onResetToDefault: () => void;
}

const ColumnOrderPanel = <T extends DataTableDataItem>({
  columns,
  isCurrentlyDefault,
  onOrderChange,
  onClose,
  onResetToDefault,
}: ColumnOrderPanelProps<T>) => {
  const { t } = useTranslation(['common', 'glossary', 'salesAndPurchasingVehicles']);

  const onSortEnd = (oldIndex: number, newIndex: number) => {
    const next = arrayMoveImmutable(cloneDeep(columns), oldIndex, newIndex).map((item, i) => ({
      ...item,
      order: i,
    }));
    onOrderChange(next);
  };

  const size = columns.length;

  const onClickUp = (i: number) => onSortEnd(i, i - 1);
  const onClickDown = (i: number) => onSortEnd(i, i + 1);

  return (
    <Panel title={t('customize', { ns: 'common' })} onClose={onClose}>
      <StackMedium>
        <Text $size="small">{t('customizePanel.help', { ns: 'salesAndPurchasingVehicles' })}</Text>
      </StackMedium>
      <StackMedium>
        <Button
          onClick={onResetToDefault}
          title={t('close', { ns: 'common' })}
          $fullWidth
          disabled={isCurrentlyDefault}
          $tone="outline"
        >
          {t('resetToDefault', { ns: 'common' })}
        </Button>
      </StackMedium>
      <StackMedium>
        <SortableList
          allowDrag
          onSortEnd={onSortEnd}
          lockAxis="y"
          draggedItemClassName="sortable-dragged-in-panel"
        >
          <SeparatorList $spacing={1}>
            {columns.map(
              ({ id, columnDef, getIsVisible, getToggleVisibilityHandler, getCanHide }, index) => {
                const canHide = getCanHide();
                const forceShow = !!columnDef.meta?.forceShow;
                const columnName = columnDef.meta?.headerAsString || id;

                return (
                  <SortableItem key={id}>
                    <SeparatorListItem>
                      <Columns $spacing={2} $valign="center">
                        <Column $noShrink>
                          <SortableKnob>
                            <Handle>
                              <IconDragAndDropHandle />
                            </Handle>
                          </SortableKnob>
                        </Column>
                        <Column $noShrink>
                          <SortActions
                            disabledUp={index === 0}
                            disabledDown={index === size - 1}
                            onClickUp={() => onClickUp(index)}
                            onClickDown={() => onClickDown(index)}
                          />
                        </Column>
                        <Column $grow>
                          <Text $weight="bold">
                            {canHide && !forceShow ? (
                              <FormGroup key={id}>
                                <InputCheckbox
                                  id={`column-${id}`}
                                  checked={getIsVisible()}
                                  onChange={getToggleVisibilityHandler()}
                                >
                                  {columnName}
                                </InputCheckbox>
                              </FormGroup>
                            ) : (
                              columnName
                            )}
                          </Text>
                        </Column>
                      </Columns>
                    </SeparatorListItem>
                  </SortableItem>
                );
              },
            )}
          </SeparatorList>
        </SortableList>
      </StackMedium>
    </Panel>
  );
};

export default ColumnOrderPanel;
